// VerdictChip — colored status chip + plain headline + "judged against" line.
//
// Load-bearing: unknown/stale (and any unrecognized status) render as the
// dignified slate NEUTRAL tone, NOT error red. Never a bare word.
import React from "react";

import type { VerdictResolution } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { VERDICT_TONE_COLORS } from "./honestyTokens";

export interface VerdictChipProps {
  resolution: VerdictResolution;
  /** hide the headline/basis lines and render only the chip. */
  chipOnly?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  on_target: "On target",
  elevated: "Elevated",
  fault: "Fault",
  stale: "Stale",
  cold: "Cold",
  unknown: "Unknown",
};

export const VerdictChip: React.FC<VerdictChipProps> = ({ resolution, chipOnly }) => {
  const { translate } = useTranslation("machineEvidence");
  const tone = VERDICT_TONE_COLORS[resolution.tone];
  const label = translate(`verdict.status.${resolution.status}`, {
    defaultValue: STATUS_LABELS[resolution.status] ?? "Unknown",
  }) as string;
  // The payload headline renders as-is (bot data); only the client "Status
  // unknown" FALLBACK is translated. The basis is payload data interpolated into
  // the translated "Judged against {basis}" prefix — no double-translation.
  const headlineText = resolution.headlineIsFallback
    ? (translate("verdict.statusUnknown", { defaultValue: "Status unknown" }) as string)
    : resolution.headline;
  const basisLine = resolution.basis
    ? (translate("verdict.judgedAgainst", {
        basis: resolution.basis,
        defaultValue: "Judged against {{basis}}",
      }) as string)
    : null;

  return (
    <div className="msc-verdict" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        className={`msc-verdict-chip msc-verdict-${resolution.status}`}
        data-verdict-tone={resolution.tone}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 10px",
          borderRadius: dt.radiusPill,
          fontFamily: dt.fontMono,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          color: tone.fg,
          background: tone.bg,
          border: `1px solid ${tone.border}`,
        }}
      >
        {label}
      </span>
      {!chipOnly && (
        <>
          <span style={{ color: dt.textPrimary, fontSize: 14, fontWeight: 500 }}>
            {headlineText}
          </span>
          {resolution.detail ? (
            <span style={{ color: dt.textSecondary, fontSize: 13 }}>{resolution.detail}</span>
          ) : null}
          {basisLine ? (
            <span style={{ color: dt.textMuted, fontSize: 12, fontStyle: "italic" }}>
              {basisLine}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
};

export default VerdictChip;
